import { useState, useEffect, useCallback } from "react";
import { useOutletContext } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  Workflow,
  Loader2,
  Plus,
  Play,
  Pause,
  Trash2,
  Settings2,
  Zap,
  Bell,
  FileText,
  Shield,
  TrendingUp,
  Calendar,
  Clock,
  CheckCircle,
  XCircle,
  ChevronRight,
  Copy,
  ToggleLeft,
  ToggleRight,
  AlertTriangle,
  Target,
  Brain,
  Sparkles
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../components/ui/tabs";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TRIGGER_TYPES = {
  viral_content: { label: "Viral Content Detected", icon: TrendingUp, color: "text-emerald-500" },
  trend_detected: { label: "New Trend Detected", icon: Sparkles, color: "text-purple-500" },
  competitor_activity: { label: "Competitor Activity", icon: Target, color: "text-blue-500" },
  sentiment_change: { label: "Sentiment Shift", icon: AlertTriangle, color: "text-amber-500" },
  win_loss_recorded: { label: "Win/Loss Recorded", icon: CheckCircle, color: "text-emerald-500" },
  scheduled: { label: "Scheduled", icon: Calendar, color: "text-gray-500" }
};

const ACTION_TYPES = {
  send_notification: { label: "Send Notification", icon: Bell },
  create_alert: { label: "Create Alert", icon: AlertTriangle },
  generate_content: { label: "Generate Content", icon: FileText },
  create_battlecard: { label: "Update Battlecard", icon: Shield },
  schedule_analysis: { label: "Schedule Analysis", icon: Brain },
  update_playbook: { label: "Update Playbook", icon: Target },
  run_intelligence_scan: { label: "Run Intelligence Scan", icon: Zap }
};

export default function WorkflowAutomation() {
  const { darkMode, region, brand } = useOutletContext() || { darkMode: true, region: 'UAE', brand: 'blackswan' };
  const [rules, setRules] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [executions, setExecutions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("rules");
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [executingRule, setExecutingRule] = useState(null);

  // Form state for new rule
  const [newRule, setNewRule] = useState({
    name: "",
    description: "",
    triggerType: "viral_content",
    triggerConditions: {},
    actions: [{ type: "send_notification", parameters: {} }]
  });

  const fetchData = useCallback(async () => {
    try {
      const [rulesRes, templatesRes, executionsRes] = await Promise.all([
        axios.get(`${API}/automation/rules/${brand}/${region}`),
        axios.get(`${API}/automation/templates`),
        axios.get(`${API}/automation/executions/${brand}/${region}?limit=20`)
      ]);
      setRules(rulesRes.data.rules || []);
      setTemplates(templatesRes.data.templates || []);
      setExecutions(executionsRes.data.executions || []);
    } catch (error) {
      console.error("Error fetching automation data:", error);
    } finally {
      setLoading(false);
    }
  }, [brand, region]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const toggleRule = async (ruleId) => {
    try {
      await axios.put(`${API}/automation/rules/${brand}/${region}/${ruleId}/toggle`);
      toast.success("Rule toggled");
      fetchData();
    } catch (error) {
      toast.error("Failed to toggle rule");
    }
  };

  const executeRule = async (ruleId) => {
    setExecutingRule(ruleId);
    try {
      await axios.post(`${API}/automation/rules/${brand}/${region}/${ruleId}/execute`);
      toast.success("Workflow execution started!");
      fetchData();
    } catch (error) {
      toast.error("Failed to execute workflow");
    } finally {
      setExecutingRule(null);
    }
  };

  const deleteRule = async (ruleId) => {
    if (!window.confirm("Are you sure you want to delete this rule?")) return;
    try {
      await axios.delete(`${API}/automation/rules/${brand}/${region}/${ruleId}`);
      toast.success("Rule deleted");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete rule");
    }
  };

  const createRuleFromTemplate = async (template) => {
    try {
      const rule = {
        name: template.name,
        description: template.description,
        trigger: template.trigger,
        actions: template.actions,
        is_enabled: true
      };
      await axios.post(`${API}/automation/rules/${brand}/${region}`, rule);
      toast.success("Rule created from template!");
      setCreateDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error("Failed to create rule");
    }
  };

  const createCustomRule = async () => {
    try {
      const rule = {
        name: newRule.name,
        description: newRule.description,
        trigger: {
          type: newRule.triggerType,
          conditions: newRule.triggerConditions
        },
        actions: newRule.actions,
        is_enabled: true
      };
      await axios.post(`${API}/automation/rules/${brand}/${region}`, rule);
      toast.success("Custom rule created!");
      setCreateDialogOpen(false);
      setNewRule({
        name: "",
        description: "",
        triggerType: "viral_content",
        triggerConditions: {},
        actions: [{ type: "send_notification", parameters: {} }]
      });
      fetchData();
    } catch (error) {
      toast.error("Failed to create rule");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="workflow-loading">
        <Loader2 className="w-8 h-8 animate-spin text-[#DC143C]" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="workflow-automation-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className={`text-2xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
            Workflow Automation
          </h1>
          <p className={`text-sm mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
            Create "If This, Then That" automation rules for your competitive intelligence
          </p>
        </div>
        <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
          <DialogTrigger asChild>
            <Button className="bg-[#DC143C] hover:bg-[#b91031]" data-testid="create-rule-btn">
              <Plus className="w-4 h-4 mr-2" />
              Create Rule
            </Button>
          </DialogTrigger>
          <DialogContent className={`max-w-2xl ${darkMode ? 'bg-[#161b22] border-[#30363d]' : ''}`}>
            <DialogHeader>
              <DialogTitle className={darkMode ? 'text-white' : ''}>Create Automation Rule</DialogTitle>
              <DialogDescription>
                Choose a template or create a custom rule
              </DialogDescription>
            </DialogHeader>
            
            <Tabs defaultValue="templates">
              <TabsList className={darkMode ? 'bg-[#21262d]' : ''}>
                <TabsTrigger value="templates">Templates</TabsTrigger>
                <TabsTrigger value="custom">Custom Rule</TabsTrigger>
              </TabsList>
              
              <TabsContent value="templates" className="mt-4 max-h-[400px] overflow-y-auto">
                <div className="space-y-3">
                  {templates.map((template) => {
                    const TriggerIcon = TRIGGER_TYPES[template.trigger.type]?.icon || Zap;
                    return (
                      <div 
                        key={template.id}
                        className={`p-4 rounded-lg border cursor-pointer transition-colors ${
                          darkMode 
                            ? 'bg-[#21262d] border-[#30363d] hover:border-[#DC143C]' 
                            : 'bg-gray-50 border-gray-200 hover:border-[#DC143C]'
                        }`}
                        onClick={() => createRuleFromTemplate(template)}
                      >
                        <div className="flex items-start gap-3">
                          <div className={`p-2 rounded-lg ${darkMode ? 'bg-[#30363d]' : 'bg-white'}`}>
                            <TriggerIcon className={`w-5 h-5 ${TRIGGER_TYPES[template.trigger.type]?.color || 'text-gray-500'}`} />
                          </div>
                          <div className="flex-1">
                            <h4 className={`font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                              {template.name}
                            </h4>
                            <p className={`text-sm mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                              {template.description}
                            </p>
                            <div className="flex items-center gap-2 mt-2">
                              <Badge className="bg-blue-500/10 text-blue-500 border-0 text-xs">
                                {template.category}
                              </Badge>
                              <span className={`text-xs ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                                {template.actions.length} action{template.actions.length > 1 ? 's' : ''}
                              </span>
                            </div>
                          </div>
                          <ChevronRight className={`w-5 h-5 ${darkMode ? 'text-gray-600' : 'text-gray-400'}`} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </TabsContent>
              
              <TabsContent value="custom" className="mt-4 space-y-4">
                <div className="space-y-2">
                  <Label className={darkMode ? 'text-gray-300' : ''}>Rule Name</Label>
                  <Input 
                    value={newRule.name}
                    onChange={(e) => setNewRule({...newRule, name: e.target.value})}
                    placeholder="My automation rule"
                    className={darkMode ? 'bg-[#21262d] border-[#30363d] text-white' : ''}
                  />
                </div>
                
                <div className="space-y-2">
                  <Label className={darkMode ? 'text-gray-300' : ''}>Description</Label>
                  <Textarea 
                    value={newRule.description}
                    onChange={(e) => setNewRule({...newRule, description: e.target.value})}
                    placeholder="What does this rule do?"
                    className={darkMode ? 'bg-[#21262d] border-[#30363d] text-white' : ''}
                  />
                </div>
                
                <div className="space-y-2">
                  <Label className={darkMode ? 'text-gray-300' : ''}>When this happens (Trigger)</Label>
                  <Select 
                    value={newRule.triggerType}
                    onValueChange={(v) => setNewRule({...newRule, triggerType: v})}
                  >
                    <SelectTrigger className={darkMode ? 'bg-[#21262d] border-[#30363d] text-white' : ''}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className={darkMode ? 'bg-[#21262d] border-[#30363d]' : ''}>
                      {Object.entries(TRIGGER_TYPES).map(([key, config]) => (
                        <SelectItem key={key} value={key} className={darkMode ? 'text-white' : ''}>
                          {config.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="space-y-2">
                  <Label className={darkMode ? 'text-gray-300' : ''}>Do this (Action)</Label>
                  <Select 
                    value={newRule.actions[0]?.type}
                    onValueChange={(v) => setNewRule({...newRule, actions: [{ type: v, parameters: {} }]})}
                  >
                    <SelectTrigger className={darkMode ? 'bg-[#21262d] border-[#30363d] text-white' : ''}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className={darkMode ? 'bg-[#21262d] border-[#30363d]' : ''}>
                      {Object.entries(ACTION_TYPES).map(([key, config]) => (
                        <SelectItem key={key} value={key} className={darkMode ? 'text-white' : ''}>
                          {config.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <DialogFooter className="mt-6">
                  <Button 
                    onClick={createCustomRule}
                    disabled={!newRule.name}
                    className="bg-[#DC143C] hover:bg-[#b91031]"
                  >
                    Create Rule
                  </Button>
                </DialogFooter>
              </TabsContent>
            </Tabs>
          </DialogContent>
        </Dialog>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-4 gap-4">
        <Card className={darkMode ? 'bg-[#161b22] border-[#30363d]' : ''}>
          <CardContent className="p-5">
            <div className="flex items-center gap-3">
              <div className="p-3 rounded-xl bg-[#DC143C]/10">
                <Workflow className="w-5 h-5 text-[#DC143C]" />
              </div>
              <div>
                <p className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Total Rules</p>
                <p className={`text-2xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                  {rules.length}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className={darkMode ? 'bg-[#161b22] border-[#30363d]' : ''}>
          <CardContent className="p-5">
            <div className="flex items-center gap-3">
              <div className="p-3 rounded-xl bg-emerald-500/10">
                <Play className="w-5 h-5 text-emerald-500" />
              </div>
              <div>
                <p className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Active</p>
                <p className={`text-2xl font-bold text-emerald-500`}>
                  {rules.filter(r => r.is_enabled).length}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className={darkMode ? 'bg-[#161b22] border-[#30363d]' : ''}>
          <CardContent className="p-5">
            <div className="flex items-center gap-3">
              <div className="p-3 rounded-xl bg-amber-500/10">
                <Pause className="w-5 h-5 text-amber-500" />
              </div>
              <div>
                <p className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Paused</p>
                <p className={`text-2xl font-bold text-amber-500`}>
                  {rules.filter(r => !r.is_enabled).length}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className={darkMode ? 'bg-[#161b22] border-[#30363d]' : ''}>
          <CardContent className="p-5">
            <div className="flex items-center gap-3">
              <div className="p-3 rounded-xl bg-purple-500/10">
                <Zap className="w-5 h-5 text-purple-500" />
              </div>
              <div>
                <p className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Executions</p>
                <p className={`text-2xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                  {executions.length}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className={darkMode ? 'bg-[#161b22]' : ''}>
          <TabsTrigger value="rules">My Rules</TabsTrigger>
          <TabsTrigger value="executions">Execution History</TabsTrigger>
        </TabsList>

        {/* Rules Tab */}
        <TabsContent value="rules" className="mt-4">
          {rules.length > 0 ? (
            <div className="space-y-4">
              {rules.map((rule) => {
                const TriggerConfig = TRIGGER_TYPES[rule.trigger?.type] || { icon: Zap, label: rule.trigger?.type, color: 'text-gray-500' };
                const TriggerIcon = TriggerConfig.icon;
                
                return (
                  <Card 
                    key={rule.id} 
                    className={`${darkMode ? 'bg-[#161b22] border-[#30363d]' : ''} ${!rule.is_enabled ? 'opacity-60' : ''}`}
                  >
                    <CardContent className="p-5">
                      <div className="flex items-start justify-between">
                        <div className="flex items-start gap-4">
                          <div className={`p-3 rounded-xl ${rule.is_enabled ? 'bg-[#DC143C]/10' : darkMode ? 'bg-[#21262d]' : 'bg-gray-100'}`}>
                            <TriggerIcon className={`w-6 h-6 ${rule.is_enabled ? TriggerConfig.color : 'text-gray-500'}`} />
                          </div>
                          <div>
                            <div className="flex items-center gap-2">
                              <h3 className={`font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                                {rule.name}
                              </h3>
                              <Badge className={`border-0 ${rule.is_enabled ? 'bg-emerald-500/10 text-emerald-500' : 'bg-gray-500/10 text-gray-500'}`}>
                                {rule.is_enabled ? 'Active' : 'Paused'}
                              </Badge>
                            </div>
                            <p className={`text-sm mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                              {rule.description || `When ${TriggerConfig.label} → ${rule.actions?.length || 0} action(s)`}
                            </p>
                            <div className="flex items-center gap-4 mt-3">
                              <span className={`text-xs flex items-center gap-1 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                                <Clock className="w-3 h-3" />
                                {rule.execution_count || 0} executions
                              </span>
                              {rule.last_execution && (
                                <span className={`text-xs ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                                  Last: {new Date(rule.last_execution).toLocaleDateString()}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => executeRule(rule.id)}
                            disabled={executingRule === rule.id}
                            className={darkMode ? 'hover:bg-[#21262d]' : ''}
                            data-testid={`execute-rule-${rule.id}`}
                          >
                            {executingRule === rule.id ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <Play className="w-4 h-4" />
                            )}
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => toggleRule(rule.id)}
                            className={darkMode ? 'hover:bg-[#21262d]' : ''}
                          >
                            {rule.is_enabled ? (
                              <ToggleRight className="w-5 h-5 text-emerald-500" />
                            ) : (
                              <ToggleLeft className="w-5 h-5 text-gray-500" />
                            )}
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => deleteRule(rule.id)}
                            className={`hover:text-red-500 ${darkMode ? 'hover:bg-[#21262d]' : ''}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                      
                      {/* Actions Preview */}
                      <div className={`mt-4 pt-4 border-t ${darkMode ? 'border-[#30363d]' : 'border-gray-200'}`}>
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className={`text-xs font-medium ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                            Actions:
                          </span>
                          {rule.actions?.map((action, idx) => {
                            const ActionConfig = ACTION_TYPES[action.type] || { icon: Zap, label: action.type };
                            const ActionIcon = ActionConfig.icon;
                            return (
                              <Badge key={idx} variant="outline" className="flex items-center gap-1">
                                <ActionIcon className="w-3 h-3" />
                                {ActionConfig.label}
                              </Badge>
                            );
                          })}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          ) : (
            <Card className={darkMode ? 'bg-[#161b22] border-[#30363d]' : ''}>
              <CardContent className="p-12 text-center">
                <Workflow className={`w-16 h-16 mx-auto mb-4 ${darkMode ? 'text-gray-600' : 'text-gray-300'}`} />
                <h3 className={`text-lg font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                  No automation rules yet
                </h3>
                <p className={`text-sm mt-2 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                  Create your first "If This, Then That" rule to automate your competitive intelligence
                </p>
                <Button 
                  className="mt-6 bg-[#DC143C] hover:bg-[#b91031]"
                  onClick={() => setCreateDialogOpen(true)}
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Create Your First Rule
                </Button>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Executions Tab */}
        <TabsContent value="executions" className="mt-4">
          <Card className={darkMode ? 'bg-[#161b22] border-[#30363d]' : ''}>
            <CardHeader>
              <CardTitle className={darkMode ? 'text-white' : ''}>Execution History</CardTitle>
              <CardDescription>Recent workflow executions</CardDescription>
            </CardHeader>
            <CardContent>
              {executions.length > 0 ? (
                <div className="space-y-3">
                  {executions.map((exec) => (
                    <div 
                      key={exec.id}
                      className={`p-4 rounded-lg ${darkMode ? 'bg-[#21262d]' : 'bg-gray-50'}`}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex items-center gap-3">
                          {exec.status === 'completed' ? (
                            <CheckCircle className="w-5 h-5 text-emerald-500" />
                          ) : exec.status === 'partial' ? (
                            <AlertTriangle className="w-5 h-5 text-amber-500" />
                          ) : (
                            <XCircle className="w-5 h-5 text-red-500" />
                          )}
                          <div>
                            <p className={`font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                              {exec.rule_name}
                            </p>
                            <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                              {exec.actions_executed} action(s) executed • {exec.trigger_source}
                            </p>
                          </div>
                        </div>
                        <span className={`text-xs ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                          {new Date(exec.executed_at).toLocaleString()}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className={`text-center py-12 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                  <Clock className="w-12 h-12 mx-auto mb-3 opacity-30" />
                  <p>No executions yet</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
